import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import ProductImage from '@/components/order/ProductImage'
import { money } from '@/lib/money'
import type { StorefrontProduct } from '@/types'

/** Marketplace card - the "Starting from" price is the best (top tier) per-piece price. */
export default function ProductCard({ product }: { product: StorefrontProduct }) {
  return (
    <Link
      to={`/p/${product.id}`}
      className="group box flex flex-col p-3 transition-shadow hover:shadow-[0_3px_20px_rgba(30,58,138,0.15)] sm:p-4"
    >
      {/* The badge is positioned against this wrapper, never against the card:
          .box sets position: relative late in the cascade, so an absolute child
          of the card itself lands in the viewport corner. */}
      <div className="relative">
        <ProductImage product={product} className="aspect-square w-full" />
        <span className="absolute right-2 top-2 rounded-full bg-white/90 px-2.5 py-0.5 text-2xs font-semibold text-foreground shadow-sm backdrop-blur-sm">
          {product.category}
        </span>
        {/* Hover invite, reference style: slides up over the photo. Pointer
            devices only - on touch the whole card is already the link. */}
        <span className="pointer-events-none absolute inset-x-2 bottom-2 hidden translate-y-2 items-center justify-between rounded-lg bg-white px-4 py-3 text-sm font-bold text-foreground opacity-0 shadow-md transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 sm:flex">
          Shop now
          <ArrowRight className="size-4" />
        </span>
      </div>
      <p className="mt-3 text-sm font-bold sm:text-base">{product.name}</p>
      <p className="mt-1 truncate text-xs text-foreground/60">Sizes {product.sizes.join(', ')}</p>
      {/* "Starting" is dropped below sm - the full phrase wraps in the ~150px
          cards of the two-column mobile grid. An agreed price is exact rather
          than a starting point, so it drops the prefix entirely. */}
      {product.special_price != null ? (
        <p className="mt-auto whitespace-nowrap pt-3 text-sm text-foreground/60">
          <span className="text-base font-extrabold text-primary sm:text-lg">{money(product.special_price)}</span>
          {product.list_price != null && product.list_price > product.special_price && (
            <span className="ml-1.5 text-xs text-foreground/40 line-through">{money(product.list_price)}</span>
          )}
        </p>
      ) : (
        <p className="mt-auto whitespace-nowrap pt-3 text-sm text-foreground/60">
          <span className="sm:hidden">From</span>
          <span className="hidden sm:inline">Starting from</span>{' '}
          <span className="text-base font-extrabold text-primary sm:text-lg">{money(product.from_price)}</span>
        </p>
      )}
    </Link>
  )
}
